import { defineStore } from 'pinia';
import { RouteRecordRaw } from 'vue-router';

import router, { allRoutes } from '@/router';
import { store } from '@/store';

/**
 * 根据用户角色过滤路由
 * @param routes 所有路由配置
 * @param roles 用户角色列表
 * @returns 返回可访问的路由和需要移除的路由
 */
function filterPermissionsRouters(routes: Array<RouteRecordRaw>, roles: Array<unknown>) {
  const res: Array<RouteRecordRaw> = [];
  const removeRoutes: Array<RouteRecordRaw> = [];
  routes.forEach((route) => {
    const children: Array<RouteRecordRaw> = [];
    route.children?.forEach((childRouter) => {
      // 获取路由的权限码，优先使用meta中的roleCode，如果没有则使用路由名称
      const roleCode = childRouter.meta?.roleCode || childRouter.name;
      // 如果用户角色中包含该路由的权限码，则添加到可访问路由中
      if (roles.indexOf(roleCode) !== -1) {
        children.push(childRouter);
      } else {
        // 否则添加到需要移除的路由中
        removeRoutes.push(childRouter);
      }
    });
    // 如果该路由有可访问的子路由，则将该路由也添加到可访问路由中
    if (children.length > 0) {
      route.children = children;
      res.push(route);
    }
  });
  return { accessedRouters: res, removeRoutes };
}

/**
 * 权限管理Store
 * 用于管理用户可访问的路由和权限控制
 */
export const usePermissionStore = defineStore('permission', {
  state: () => ({
    // 免登录白名单路由
    whiteListRouters: ['/login', '/index', '/apply'],
    // 可访问的路由列表
    routers: [] as Array<RouteRecordRaw>,
    // 被移除的路由列表
    removeRoutes: [] as Array<RouteRecordRaw>,
    initialized: false,
  }),
  actions: {
    /**
     * 初始化路由权限
     * @param roles 用户角色列表
     */
    async initRoutes(roles: Array<unknown>) {
      let accessedRouters = [];

      let removeRoutes: Array<RouteRecordRaw> = [];
      // 特殊角色'all'拥有所有路由权限
      if (roles.includes('all')) {
        accessedRouters = allRoutes;
      } else {
        // 根据用户角色过滤路由
        const res = filterPermissionsRouters(allRoutes, roles);
        accessedRouters = res.accessedRouters;
        removeRoutes = res.removeRoutes;
      }

      // 更新store中的路由信息
      this.routers = accessedRouters;
      this.removeRoutes = removeRoutes;
      this.initialized = true;

      // 从路由表中移除无权访问的路由
      removeRoutes.forEach((item: RouteRecordRaw) => {
        if (router.hasRoute(item.name)) {
          router.removeRoute(item.name);
        }
      });
    },
    /**
     * 恢复被移除的路由
     * 通常在用户注销或切换角色时调用
     */
    async restore() {
      this.removeRoutes.forEach((item) => {
        router.addRoute(item as RouteRecordRaw);
      });
      this.routers = [];
      this.removeRoutes = [];
      this.initialized = false;
    },
  },
});

/**
 * 获取权限Store的辅助函数
 * @returns 权限Store实例
 */
export function getPermissionStore() {
  return usePermissionStore(store);
}
